import { useState, useEffect, useCallback } from 'react'
import { marketingApi } from '../api/marketing'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { fmtFechaHora } from '../utils/fecha'
import { colors, font, text, space, radius, sx } from '../design/tokens'

/**
 * MARKETING → Newsletter Experiencias (sugerencia #118, Brenda, 24 oct 2026):
 * quién se anotó desde la pestaña Experiencias de ligier.com.ar para recibir
 * novedades de cenas, eventos, degustaciones y viajes. Solo lectura: el alta la
 * hace la tienda y la baja el link de cada mail.
 */

const ESTADO = {
  suscripto: { label: 'Suscripto', bg: colors.okBg, fg: colors.ok },
  baja: { label: 'Se dio de baja', bg: colors.bgSoft, fg: colors.textSec },
}

export default function MktNewsletterExperienciasPage() {
  const [q, setQ] = useState('')
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)

  const cargar = useCallback(async () => {
    try {
      const r = await marketingApi.newsletterExperiencias({ q: q || undefined })
      setData(r.data)
      setError(false)
    } catch { setError(true) }
  }, [q])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(cargar, 60000)

  const r = data?.resumen
  const contactos = data?.contactos ?? []

  return (
    <div>
      <div style={st.headerRow}>
        <div>
          <h1 style={st.title}>Newsletter Experiencias</h1>
          <p style={st.subtitle}>
            Personas que se anotaron desde la pestaña Experiencias de ligier.com.ar para recibir novedades de cenas,
            eventos, degustaciones y viajes. Quedan en la lista «{data?.lista ?? 'Newsletter Experiencias'}» del módulo Marketing.
          </p>
        </div>
        {r && (
          <div style={st.kpis}>
            <Kpi n={r.suscriptos} label="suscriptos" />
            <Kpi n={r.ultimos_7_dias} label="últimos 7 días" />
            <Kpi n={r.bajas} label="bajas" />
          </div>
        )}
      </div>

      <div style={sx.card}>
        <div style={st.toolbar}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por email o nombre…" style={st.search} />
          <span style={st.metaTxt}>{contactos.length} contacto{contactos.length === 1 ? '' : 's'}</span>
        </div>

        {error && <div style={st.errTxt}>No se pudo cargar la lista.</div>}

        <div style={{ overflowX: 'auto' }}>
          <table style={sx.table}>
            <thead>
              <tr>
                <th style={sx.th}>Email</th>
                <th style={sx.th}>Nombre</th>
                <th style={sx.th}>Cliente</th>
                <th style={sx.th}>Estado</th>
                <th style={sx.th}>Se anotó</th>
              </tr>
            </thead>
            <tbody>
              {data && contactos.length === 0 && (
                <tr><td colSpan={5} style={st.empty}>
                  {q ? 'Nadie coincide con la búsqueda.' : 'Todavía nadie se anotó. El formulario está en ligier.com.ar/experiencias, después de las fechas.'}
                </td></tr>
              )}
              {contactos.map(c => {
                const e = ESTADO[c.estado === 'suscripto' ? 'suscripto' : 'baja']
                return (
                  <tr key={c.id}>
                    <td style={{ ...sx.td, ...sx.mono }}>{c.email}</td>
                    <td style={sx.td}>{c.nombre || <span style={{ color: colors.textMeta }}>—</span>}</td>
                    <td style={sx.td}>
                      {c.cliente_id
                        ? <span style={sx.mono}>{c.codigo_presea || 'con ficha'}</span>
                        : <span style={{ color: colors.textMeta }}>—</span>}
                    </td>
                    <td style={sx.td}><span style={{ ...st.badge, background: e.bg, color: e.fg }}>{e.label}</span></td>
                    <td style={{ ...sx.td, ...sx.mono, color: colors.textSec }}>{c.alta ? fmtFechaHora(c.alta) : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function Kpi({ n, label }) {
  return (
    <div style={st.kpi}>
      <div style={st.kpiN}>{n ?? '—'}</div>
      <div style={st.kpiL}>{label}</div>
    </div>
  )
}

const st = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.xl, marginBottom: space.xl, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  subtitle: { margin: '4px 0 0', fontSize: text.body, color: colors.textSec, maxWidth: 640, lineHeight: 1.5 },
  kpis: { display: 'flex', gap: space.xl },
  kpi: { textAlign: 'right' },
  kpiN: { fontSize: 22, fontWeight: 700, color: colors.text, fontFamily: font.mono },
  kpiL: { fontSize: text.meta, color: colors.textSec },
  toolbar: { display: 'flex', alignItems: 'center', gap: space.lg, padding: `0 0 ${space.lg}px`, borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap' },
  search: { flex: 1, minWidth: 220, maxWidth: 380, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, border: `1px solid ${colors.textPlh}`, fontSize: text.body, fontFamily: font.sans, outline: 'none' },
  metaTxt: { fontSize: text.meta, color: colors.textSec, marginLeft: 'auto' },
  errTxt: { color: colors.err, fontSize: text.sec, padding: `${space.md}px 0` },
  empty: { padding: space['3xl'], textAlign: 'center', color: colors.textSec, fontSize: text.sec },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: radius.md, fontSize: text.meta, fontWeight: 600 },
}
